import React, { type RefObject, useMemo } from 'react';

type useIntersectionObserverProps = {
  rootId?: string;
  target: RefObject<HTMLElement>;
  onIntersect: IntersectionObserverCallback;
  threshold?: number;
  rootMargin?: string;
};

export default function useIntersectionObserver({
  rootId,
  target,
  onIntersect,
  threshold = 1.0,
  rootMargin = '0px',
}: useIntersectionObserverProps) {
  const observer = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const root = document && rootId ? document.getElementById(rootId) : null;
    return new IntersectionObserver(onIntersect, {
      root,
      rootMargin,
      threshold,
    });
  }, [onIntersect, rootId, rootMargin, threshold]);

  React.useEffect(() => {
    if (!observer) return;
    if (!target) return;
    const el = target.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (!el) {
        return;
      }
      observer.unobserve(el);
    };
  }, [observer, target]);
}
